import { ToggleButton } from "@/components/ui";
import { ContactInfoFields } from "@/components/ui/ContactInfoFields";
import { FieldError } from "@/components/ui/FieldError";
import { FormField } from "./FormField";
import { StepFooter } from "./StepFooter";
import { contactMethods } from "./types";
import { validateContactStep } from "./validators";
import type { StepProps } from "./step-props";
import step from "./step.module.css";
import styles from "./ContactStep.module.css";

interface ContactStepProps extends StepProps {
  /** Contact is the final step — Continue submits the inquiry. */
  submitError: string | null;
  isSubmitting: boolean;
}

export function ContactStep({
  profile,
  formData,
  showValidation,
  updateField,
  onBack,
  onContinue,
  onShowValidation,
  submitError,
  isSubmitting,
}: ContactStepProps): React.JSX.Element {
  const copy = profile.contactStep;
  const errors = validateContactStep(formData);
  const methodError = showValidation ? errors.preferredContactMethod : undefined;
  // When suits is asked only for a call or a text (CONTEXT.md's Contact
  // Preference): a time to email somebody is not an answer to anything.
  const wantsACallOrText =
    formData.preferredContactMethod === "call" ||
    formData.preferredContactMethod === "text";

  const handleContinue = (): void => {
    if (Object.keys(errors).length === 0) {
      onContinue();
    } else {
      onShowValidation(Object.keys(errors));
    }
  };

  return (
    <div className={step.step}>
      <div>
        <h1 className={step.heading}>{copy.title}</h1>
        <ContactInfoFields
          contactName={formData.contactName || ""}
          contactEmail={formData.contactEmail || ""}
          contactPhone={formData.contactPhone || ""}
          showValidation={showValidation}
          labels={{
            name: copy.fields.contactName.label,
            email: copy.fields.contactEmail.label,
            phone: copy.fields.contactPhone.label,
          }}
          nameError={errors.contactName}
          emailError={errors.contactEmail}
          phoneError={errors.contactPhone}
          onNameChange={(val) => updateField("contactName", val)}
          onEmailChange={(val) => updateField("contactEmail", val)}
          onPhoneChange={(val) => updateField("contactPhone", val)}
        />

        <div className={step.group}>
          <label className={styles.label}>
            {copy.fields.preferredContactMethod.label}
          </label>
          <div
            className={styles.methods}
            role="radiogroup"
            aria-label={copy.fields.preferredContactMethod.label}
            aria-invalid={methodError ? true : undefined}
          >
            {contactMethods.map(({ value, label }) => (
              <ToggleButton
                key={value}
                pressed={formData.preferredContactMethod === value}
                onPress={() => updateField("preferredContactMethod", value)}
                className={
                  methodError && formData.preferredContactMethod !== value
                    ? styles.methodInvalid
                    : undefined
                }
              >
                {label}
              </ToggleButton>
            ))}
          </div>
          <FieldError message={methodError} />
        </div>

        {wantsACallOrText && (
          <div className={step.group}>
            <FormField
              label={copy.fields.contactWhen.label}
              field="contactWhen"
              formData={formData}
              optional
              hint={copy.fields.contactWhen.hint}
              onChange={(val) => updateField("contactWhen", val)}
            />
          </div>
        )}
      </div>

      {submitError && (
        <p className={step.submitError}>{submitError}</p>
      )}

      <StepFooter
        onBack={onBack}
        onContinue={handleContinue}
        continueLabel={isSubmitting ? "Sending..." : "Send my enquiry"}
        loading={isSubmitting}
      />
    </div>
  );
}
